import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  Feature,
} from "@models";
import { 
  // ArmorType,
  // BaseItem,
  Overridable,
  OverridableNumber,
  Resource,
  SENSE_OPTIONS,
  SENSES,
  SpellLimitation,
  Transform,
  // Skill,
  // Tool,
  TransformInterface,
  // WeaponKeyword, 
} from "@dndModels";
import { 
  // ABILITY_SCORES,
  // PASSIVE_MAP,
  // PASSIVE_OPTIONS,
  SIZE_OPTIONS, 
  SPEED_MAP, 
  SPEED_OPTIONS, 
  TRANSFORM_FEATURE_TYPES,
  TRANSFORM_INTERFACE_FEATURE_TYPES
} from "@dndModels/Constants";

import API from "@dndUtilities/system_api";
import CommonUtilities from "@utilities/common_utilities";

import LoadingBox from "@components/input/LoadingBox";

import OverridableNumberInput from "@dndComponents/other/OverridableNumberInput";
import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
// import FilterBox from '../FilterBox';
import CheckBox from "@components/input/CheckBox";
// import CenteredMenu from '../../../../components/input/menus/CenteredMenu';
import FeatureListInput from "@dndComponents/feature/FeatureListInput";
import FeatureInput from "@dndComponents/feature/FeatureInput";
import SpellLimitInput from "@dndComponents/limitations/SpellLimitInput";

const api = API.getInstance();
const common_util = CommonUtilities.getInstance();

interface Props { 
  obj: TransformInterface;
  disabled: boolean;
  tab: string;
  change_time: number;
  can_inherit?: boolean;
  can_creature?: boolean;
  onChange: (changed: TransformInterface, log_it: boolean) => void; 
}

export default function TransformInterfaceInput({
  obj,
  disabled,
  tab,
  change_time,
  can_inherit = false,
  can_creature = false,
  onChange,
}: Props) {
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [resources, setResources] = useState<Resource[]>([]);
  // const [skills, setSkills] = useState<Skill[]>([]);
  // const [tools, setTools] = useState<Tool[]>([]);
  // const [armor_types, setArmorTypes] = useState<ArmorType[]>([]);
  // const [weapon_keywords, setWeaponKeywords] = useState<WeaponKeyword[]>([]);
  // const [base_items, setBaseItems] = useState<BaseItem[]>([]);
  const [my_change_time, setChangeTime] = useState(0);
  const [prev_change_time, setPrevChangeTime] = useState(0);
  const [prev_tab, setTab] = useState(tab);
  // const [prof_tab, setProfTab] = useState("Skills");
  // const [spell_filter, setSpellFilter] = useState<any>({});
  const [expanded, setExpanded] = useState<Feature | null>(null);
  const [my_obj, setObj] = useState(obj.clone());
  
  useEffect(() => {
    if (!fetched) {
      load();
    }
  });

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);
  
  useEffect(() => {
    if (tab !== prev_tab) {
      setTab(tab);
    }
    // eslint-disable-next-line
  }, [tab]);
  
  useEffect(() => {
    if (change_time !== prev_change_time) {
      setPrevChangeTime(change_time);
      setObj(obj.clone());
      setReloading(true);
    }
    // eslint-disable-next-line
  }, [change_time]);

  const load = () => {
    setFetched(true);
    setLoading(true);
      
    api.getSetOfObjects({ data_types: ["resource"]}).then(res => {
      setResources(res.resource as Resource[]);
      setLoading(false);
    });
  }

  const updateObj = (new_obj: TransformInterface, log_it: boolean) => {
    setObj(new_obj)
    onChange(new_obj, log_it);
    setChangeTime(common_util.get_time_number());
  }

  const renderTab = () => {
    switch (tab) {
      case "Ability Scores": return renderAbilityScores();
      case "Attributes": return renderAttributes();
      case "Abilities": return renderAbilities();
      case "Resources": return renderResources();
      // case "Proficiencies": return renderProficiencies();
      case "Senses": return renderSenses();
      case "Speeds": return renderSpeeds();
      case "Common Features": return renderTransformFeatures();
    }
  }

  const renderAbilityScores = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Current Ability Scores
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Strength"
            obj={my_obj.current_ability_scores.strength}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.current_ability_scores.strength.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Dexterity"
            obj={my_obj.current_ability_scores.dexterity}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.current_ability_scores.dexterity.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Constitution"
            obj={my_obj.current_ability_scores.constitution}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.current_ability_scores.constitution.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Intelligence"
            obj={my_obj.current_ability_scores.intelligence}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.current_ability_scores.intelligence.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Wisdom"
            obj={my_obj.current_ability_scores.wisdom}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.current_ability_scores.wisdom.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Charisma"
            obj={my_obj.current_ability_scores.charisma}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.current_ability_scores.charisma.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Max Ability Scores
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Strength"
            obj={my_obj.max_ability_scores.strength}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.max_ability_scores.strength.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Dexterity"
            obj={my_obj.max_ability_scores.dexterity}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.max_ability_scores.dexterity.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Constitution"
            obj={my_obj.max_ability_scores.constitution}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.max_ability_scores.constitution.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Intelligence"
            obj={my_obj.max_ability_scores.intelligence}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.max_ability_scores.intelligence.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Wisdom"
            obj={my_obj.max_ability_scores.wisdom}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.max_ability_scores.wisdom.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Charisma"
            obj={my_obj.max_ability_scores.charisma}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.max_ability_scores.charisma.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderAttributes = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Hit Points
        </Grid>
        <Grid
          size={{
            xs: 3
          }}>
          <CheckBox
            name="Calc Max HP"
            value={my_obj.calc_max_hp}
            disabled={disabled}
            onChange={(changed: boolean) => {
              const new_obj = obj.clone();
              new_obj.calc_max_hp = changed;
              updateObj(new_obj, false);
            }}
          />
        </Grid>
        { my_obj.calc_max_hp ?
          <>
            <Grid
              size={{
                xs: 3
              }}>
              <OverridableNumberInput
                name="Hit Dice Count"
                obj={my_obj.hit_dice_count}
                change_time={my_change_time}
                can_inherit={can_inherit}
                can_creature={can_creature}
                disabled={disabled}
                onChange={(changed: Overridable) => {
                  if (changed instanceof OverridableNumber) {
                    const new_obj = obj.clone();
                    new_obj.hit_dice_count.copy(changed);
                    updateObj(new_obj, false);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <OverridableNumberInput
                name="Hit Dice Size"
                obj={my_obj.hit_dice_size}
                change_time={my_change_time}
                can_inherit={can_inherit}
                can_creature={can_creature}
                disabled={disabled}
                onChange={(changed: Overridable) => {
                  if (changed instanceof OverridableNumber) {
                    const new_obj = obj.clone();
                    new_obj.hit_dice_size.copy(changed);
                    updateObj(new_obj, false);
                  }
                }}
              />
            </Grid>
          </>
        :
          <>
            <Grid
              size={{
                xs: 3
              }}>
              <OverridableNumberInput
                name="Current"
                obj={my_obj.current_hit_points}
                change_time={my_change_time}
                can_inherit={can_inherit}
                can_creature={can_creature}
                disabled={disabled}
                onChange={(changed: Overridable) => {
                  if (changed instanceof OverridableNumber) {
                    const new_obj = obj.clone();
                    new_obj.current_hit_points.copy(changed);
                    updateObj(new_obj, false);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 3
              }}>
              <OverridableNumberInput
                name="Max"
                obj={my_obj.max_hit_points}
                change_time={my_change_time}
                can_inherit={can_inherit}
                can_creature={can_creature}
                disabled={disabled}
                onChange={(changed: Overridable) => {
                  if (changed instanceof OverridableNumber) {
                    const new_obj = obj.clone();
                    new_obj.max_hit_points.copy(changed);
                    updateObj(new_obj, false);
                  }
                }}
              />
            </Grid>
          </>
        }
        <Grid
          size={{
            xs: 3
          }}>
          <OverridableNumberInput
            name="Temp"
            obj={my_obj.temp_hit_points}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.temp_hit_points.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Size
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          <CheckBox 
            name="Specific Options"
            value={my_obj.size.use_options}
            onChange={(changed: boolean) => {
              const new_obj = obj.clone();
              new_obj.size.use_options = changed;
              if (changed) {
                new_obj.size.options = [];
              } else {
                new_obj.size.overridable = new OverridableNumber(3);
              }
              updateObj(new_obj, false);
            }}
          />
        </Grid>
        { my_obj.size.use_options && my_obj.size.options ?
          <Grid
            size={{
              xs: 6
            }}>
            <SelectStringBox 
              name="Size Options"
              values={my_obj.size.options.map(o => `${o}`)}
              options={SIZE_OPTIONS}
              multiple
              onChange={(changed: string[]) => {
                const new_obj = obj.clone();
                new_obj.size.options = changed.map(o => +o);
                updateObj(new_obj, false);
              }}
            />
          </Grid>
        : my_obj.size.overridable &&
          <Grid
            size={{
              xs: 6
            }}>
            <OverridableNumberInput
              name=""
              obj={my_obj.size.overridable}
              change_time={my_change_time}
              can_inherit={can_inherit}
              can_creature={can_creature}
              disabled={disabled}
              onChange={(changed: Overridable) => {
                if (changed instanceof OverridableNumber) {
                  const new_obj = obj.clone();
                  new_obj.size.overridable = changed.clone();
                  updateObj(new_obj, false);
                }
              }}
            />
          </Grid>
        }
        {/* <Grid item xs={12}>
          Advantages, Min Rolls, etc.
        </Grid> */}
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Other Important Attributes
        </Grid>
        {/* <Grid item xs={4}>
          <OverridableNumberInput
            name="Max Carrying Capacity"
            obj={my_obj.max_carrying_capacity}
            change_time={my_change_time}
            can_inherit={can_inherit}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.max_carrying_capacity.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid> */}
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Armor Class"
            obj={my_obj.armor_class}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.armor_class.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 4
          }}>
          <OverridableNumberInput
            name="Proficiency Bonus"
            obj={my_obj.prof_bonus}
            change_time={my_change_time}
            can_inherit={can_inherit}
            can_creature={can_creature}
            disabled={disabled}
            onChange={(changed: Overridable) => {
              if (changed instanceof OverridableNumber) {
                const new_obj = obj.clone();
                new_obj.prof_bonus.copy(changed);
                updateObj(new_obj, false);
              }
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderTransformFeatures = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Accessible Common Character Features
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name=""
            options={ my_obj instanceof Transform ? TRANSFORM_FEATURE_TYPES : TRANSFORM_INTERFACE_FEATURE_TYPES }
            extra_options={["Default"]}
            values={Object.keys(my_obj.feature_filters)} 
            multiple
            onChange={(feature_types: string[]) => {
              const new_obj = obj.clone();
              const keys = Object.keys(obj.feature_filters);
              for (const id of feature_types.filter(o => !keys.includes(o))) {
                new_obj.feature_filters[id] = { block_all: true };
              }
              for (const id of keys.filter(o => !feature_types.includes(o))) {
                delete new_obj.feature_filters[id];
              }
              updateObj(new_obj, false);
            }}
          />
        </Grid>
        { Object.keys(my_obj.feature_filters).map((feature_type, key) => {
          return (
            <Grid
              key={key}
              container
              spacing={0}
              direction="row"
              size={{
                xs: 12
              }}>
              <Grid
                size={{
                  xs: 6
                }}>
                <CheckBox 
                  name={`Block All ${feature_type}`}
                  value={my_obj.feature_filters[feature_type].block_all === true}
                  onChange={(changed: boolean) => {
                    const new_obj = obj.clone();
                    new_obj.feature_filters[feature_type].block_all = changed;
                    updateObj(new_obj, false);
                  }}
                />
              </Grid>
              {/* <Grid item xs={6}>
                { my_obj.feature_filters[feature_type].block_all === false &&
                  <FilterBox 
                    the_filter={translateFilterForBox(my_obj.feature_filters[feature_type])}
                    fields={["effect_type","attack_type","feature_source_type"]}
                    dont_use_api
                    onChange={updateSpellFilter}
                  />
                }
              </Grid> */}
            </Grid>
          );
        })}
        <Grid
          size={{
            xs: 12
          }}>
          <FeatureListInput 
            label="Extra Common Character Feature"
            features={my_obj.features} 
            parent_id={my_obj.id} 
            parent_type="Transform"
            disabled={ disabled || my_obj.name === "" }
            onExpand={(feature: Feature) => {
              if (obj.name !== "") {
                setExpanded(feature);
              }
            }}
            onAdd={() => {
              const new_obj = obj.clone();
              const feature = new Feature();
              feature.parent_type = "Transform";
              feature.parent_id = new_obj.id;
              feature.base_id = new_obj.id;
              new_obj.features.push(feature);
              setExpanded(feature);
              updateObj(new_obj, false);
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderAbilities = () => {
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <CheckBox 
            name="Can Concentrate"
            value={my_obj.can_concentrate}
            onChange={(changed: boolean) => {
              const new_obj = obj.clone();
              new_obj.can_concentrate = changed;
              updateObj(new_obj, false);
            }}
          />
        </Grid>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Spells Accessible while Transformed
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SpellLimitInput
            obj={my_obj.spell_limit}
            disabled={disabled}
            onChange={(changed: SpellLimitation) => {
              const new_obj = obj.clone();
              new_obj.spell_limit.copy(changed);
              updateObj(new_obj, false);
            }}
          />
        </Grid>
      </Grid>
    );
  }

  const renderResources = () => {
    console.log(Object.keys(my_obj.senses));
    console.log(my_obj.slots);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Resources Accessible while Transformed
        </Grid>
        <Grid
          size={{
            xs: 6
          }}>
          { !reloading &&
            <SelectObjectBox
              name="Slot Types"
              data_type={"spell_slot_type"}
              extra_options={ can_inherit ? ["Inherit"] : ["All"] }
              values={[...my_obj.slots]} 
              multiple
              onChange={(slot_ids: string[]) => {
                const new_obj = obj.clone();
                // debugger;
                new_obj.slots = slot_ids;
                updateObj(new_obj, true);
              }}
            />
          }
        </Grid>
        <Grid
          container
          spacing={0}
          direction="row"
          size={{
            xs: 6
          }}>
          <Grid
            size={{
              xs: 12
            }}>
            <SelectObjectBox
              name="Resources"
              data_type={"resource"}
              extra_options={["Default"]}
              values={Object.keys(my_obj.resources)} 
              multiple
              display_format="{obj.use_name} ({obj.description})"
              onChange={(resource_ids: string[]) => {
                const new_obj = obj.clone();
                const keys = Object.keys(obj.resources);
                for (const id of resource_ids.filter(o => !keys.includes(o))) {
                  new_obj.resources[id] = new OverridableNumber(60);
                }
                for (const id of keys.filter(o => !resource_ids.includes(o))) {
                  delete new_obj.resources[id];
                }
                updateObj(new_obj, false);
              }}
            />
          </Grid>
          { Object.keys(my_obj.resources).map((resource_id, key) => {
            const resource = resources.find(o => o._id === resource_id);
            return (
              <Grid
                key={key}
                size={{
                  xs: 12
                }}>
                <OverridableNumberInput
                  name={ resource ? resource.name : resource_id }
                  obj={my_obj.resources[resource_id]}
                  change_time={my_change_time}
                  can_inherit={can_inherit}
                  can_creature={can_creature}
                  disabled={disabled}
                  onChange={(changed: Overridable) => {
                    if (changed instanceof OverridableNumber) {
                      const new_obj = obj.clone();
                      new_obj.resources[resource_id].copy(changed);
                      updateObj(new_obj, false);
                    }
                  }}
                />
              </Grid>
            );
          })}
        </Grid>
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Senses
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !reloading &&
            <SelectStringBox
              name="Senses"
              options={SENSE_OPTIONS}
              extra_options={["Default"]}
              values={Object.keys(my_obj.senses)} 
              multiple
              onChange={(sense_ids: string[]) => {
                const new_obj = obj.clone();
                const keys = Object.keys(obj.senses);
                for (const id of sense_ids.filter(o => !keys.includes(o))) {
                  new_obj.senses[id] = new OverridableNumber(60);
                }
                for (const id of keys.filter(o => !sense_ids.includes(o))) {
                  delete new_obj.senses[id];
                }
                updateObj(new_obj, true);
              }}
            />
          }
        </Grid>
        { Object.keys(my_obj.senses).map((sense_id, key) => {
          const sense = SENSES.find(o => o._id === sense_id);
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <OverridableNumberInput
                name={sense ? sense.name : sense_id}
                obj={my_obj.senses[sense_id]}
                change_time={my_change_time}
                can_inherit={can_inherit}
                can_creature={can_creature}
                disabled={disabled}
                onChange={(changed: Overridable) => {
                  if (changed instanceof OverridableNumber) {
                    const new_obj = obj.clone();
                    new_obj.senses[sense_id].copy(changed);
                    updateObj(new_obj, false);
                  }
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }

  const renderSenses = () => {
    console.log(Object.keys(obj.senses));
    console.log(Object.keys(my_obj.senses));
    console.log(my_obj.slots);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Senses
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          { !reloading &&
            <SelectStringBox
              name="Senses"
              options={SENSE_OPTIONS}
              extra_options={["Default"]}
              values={Object.keys(my_obj.senses)} 
              multiple
              onChange={(sense_ids: string[]) => {
                const new_obj = obj.clone();
                const keys = Object.keys(obj.senses);
                for (const id of sense_ids.filter(o => !keys.includes(o))) {
                  new_obj.senses[id] = new OverridableNumber(60);
                }
                for (const id of keys.filter(o => !sense_ids.includes(o))) {
                  delete new_obj.senses[id];
                }
                updateObj(new_obj, true);
              }}
            />
          }
        </Grid>
        { Object.keys(my_obj.senses).map((sense_id, key) => {
          const sense = SENSES.find(o => o._id === sense_id);
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <OverridableNumberInput
                name={sense ? sense.name : sense_id}
                obj={my_obj.senses[sense_id]}
                change_time={my_change_time}
                can_inherit={can_inherit}
                can_creature={can_creature}
                disabled={disabled}
                onChange={(changed: Overridable) => {
                  if (changed instanceof OverridableNumber) {
                    const new_obj = obj.clone();
                    new_obj.senses[sense_id].copy(changed);
                    updateObj(new_obj, false);
                  }
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }

  const renderSpeeds = () => {
    const speeds = Object.keys(SPEED_MAP);
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          className="header centered"
          size={{
            xs: 12
          }}>
          Speeds
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox
            name=""
            options={[{ id: "Default", text: "Default" },...SPEED_OPTIONS]}
            values={Object.keys(my_obj.speed)} 
            multiple
            onChange={(changed: string[]) => {
              const new_obj = obj.clone();
              const keys = Object.keys(obj.speed);
              for (const id of changed.filter(o => !keys.includes(o))) {
                new_obj.speed[id] = new OverridableNumber(0);
              }
              for (const id of keys.filter(o => !changed.includes(o))) {
                delete new_obj.speed[id];
              }
              updateObj(new_obj, false);
            }}
          />
        </Grid>
        { Object.keys(my_obj.speed).map((speed, key) => {
          return (
            <Grid
              key={key}
              size={{
                xs: 12
              }}>
              <OverridableNumberInput
                name={ speeds.includes(speed) ? SPEED_MAP[speed] : speed}
                obj={my_obj.speed[speed]}
                change_time={my_change_time}
                can_inherit={can_inherit}
                can_creature={can_creature}
                disabled={disabled}
                onChange={(changed: Overridable) => {
                  if (changed instanceof OverridableNumber) {
                    const new_obj = obj.clone();
                    new_obj.speed[speed].copy(changed);
                    updateObj(new_obj, false);
                  }
                }}
              />
            </Grid>
          );
        })}
      </Grid>
    );
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else if (expanded) {
    return (
      <div>
        <FeatureInput 
          label="Feature"
          parent_name={my_obj.name}
          feature={expanded} 
          disabled={disabled}
          allowed_feature_types={ my_obj instanceof Transform ? TRANSFORM_FEATURE_TYPES : TRANSFORM_INTERFACE_FEATURE_TYPES }
          onChange={(changed: Feature) => {
            const new_obj = obj.clone();
            new_obj.features = common_util.replaceInList(new_obj.features, changed.true_id, changed, "true_id");
            setExpanded(changed);
            updateObj(new_obj, false);
          }}
          onDelete={() => {
            const id = expanded.true_id;
            const new_obj = obj.clone();
            new_obj.features = new_obj.features.filter(o => o.true_id !== id);
            setExpanded(null);
            updateObj(new_obj, false);
          }}
          onDone={(target: string) => {
            if (target === "base") {
              setExpanded(null);
            } else {
              setExpanded(null);
            }
          }}
        />
      </div>
    );
  } else {
    return (
      <div>
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            { renderTab() }
          </Grid>
        </Grid>
      </div>
    ); 
  }
}